import {
	ACTIONS_CELL_SX,
	RowActions,
} from "@/shared/components/editable-table/RowActions";
import {
	EditableCell,
	editableRowSx,
} from "@/shared/components/editable-table/EditableCell";
import { useEditableRow } from "@/shared/components/editable-table/useEditableRow";
import { TableCell, TableRow } from "@mui/material";
import { DateCell, NumberCell } from "../../../shared/components/table/Cell";
import { toUtcDay } from "../../../shared/utils/date";
import {
	type BillPeriod,
	formatBillingDate,
	isDateBilled,
} from "../../../shared/utils/billPeriods";
import type { GasBill, GasBillDraft } from "../models/GasBilling";

const asFormatter = (options: Intl.NumberFormatOptions) => {
	const intl = new Intl.NumberFormat("en-US", options);
	return (value: number | null) => (value === null ? "" : intl.format(value));
};

const formatVolume = asFormatter({ maximumFractionDigits: 1 });
const formatMoney = asFormatter({
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});
const formatHeatValue = asFormatter({ maximumFractionDigits: 2 });
const formatRate = asFormatter({
	minimumFractionDigits: 2,
	maximumFractionDigits: 4,
});

interface GasBillRowProps {
	bill: GasBill;
	draft: GasBillDraft | null;
	periods: BillPeriod[];
	error: string | null;
	isLocked: boolean;
	isSaving: boolean;
	onPatch: (changes: Partial<GasBillDraft>) => void;
	onEdit: () => void;
	onCancel: () => void;
	onSave: () => void;
	onDelete: () => void;
}

export const GasBillRow = ({
	bill,
	draft,
	periods,
	error,
	isLocked,
	isSaving,
	onPatch,
	onEdit,
	onCancel,
	onSave,
	onDelete,
}: GasBillRowProps) => {
	const isEditing = draft !== null;
	const row = draft ?? bill;
	const blockedReason = isSaving
		? "Still loading, try again in a moment"
		: error;

	const editable = useEditableRow<HTMLTableRowElement>({
		isEditing,
		isLocked,
		canStartEdit: !isSaving,
		blockedReason,
		onEdit,
		onCancel,
		onSave,
	});

	const periodCell = (
		key: "ServicePeriodStart" | "ServicePeriodEnd",
		testId: string,
	) => (
		<EditableCell
			data-test-id={testId}
			sx={{ textAlign: "center" }}
			isEditing={isEditing}
			editor={
				<DateCell
					value={row[key]}
					shouldDisableDate={(day) =>
						isDateBilled(periods, toUtcDay(day), bill.GasBillId)
					}
					onChange={(value) =>
						onPatch({ [key]: value } as Partial<GasBillDraft>)
					}
				/>
			}
		>
			{formatBillingDate(row[key])}
		</EditableCell>
	);

	const figureCell = (
		key: "Mcf" | "Mmbtu" | "Cost",
		testId: string,
		integerDigits: number,
		decimals: number,
		display: (value: number | null) => string,
	) => (
		<EditableCell
			data-test-id={testId}
			sx={{ textAlign: "right" }}
			isEditing={isEditing}
			editor={
				<NumberCell
					value={row[key]}
					min={0}
					integerDigits={integerDigits}
					decimals={decimals}
					onChange={(value) =>
						onPatch({ [key]: value } as Partial<GasBillDraft>)
					}
				/>
			}
		>
			{display(row[key])}
		</EditableCell>
	);

	return (
		<TableRow
			{...editable.rowProps}
			data-test-id={`gas-bill-row-${bill.GasBillId}`}
			sx={editableRowSx({ isEditing, isLocked })}
		>
			{periodCell("ServicePeriodStart", "bill-start")}
			{periodCell("ServicePeriodEnd", "bill-end")}
			{figureCell("Mcf", "bill-mcf", 15, 3, formatVolume)}
			{figureCell("Mmbtu", "bill-mmbtu", 15, 3, formatVolume)}
			{figureCell("Cost", "bill-cost", 16, 2, formatMoney)}
			<TableCell
				data-test-id="bill-heat-value"
				sx={{
					color: "#7a7a7a",
					backgroundColor: "grey.100",
					textAlign: "right",
				}}
			>
				{formatHeatValue(
					row.Mcf === null || row.Mmbtu === null || row.Mcf === 0
						? null
						: (row.Mmbtu * 1000) / row.Mcf,
				)}
			</TableCell>
			<TableCell
				data-test-id="bill-cost-per-mcf"
				sx={{
					color: "#7a7a7a",
					backgroundColor: "grey.100",
					textAlign: "right",
				}}
			>
				{formatRate(
					row.Mcf === null || row.Cost === null || row.Mcf === 0
						? null
						: row.Cost / row.Mcf,
				)}
			</TableCell>
			<TableCell sx={ACTIONS_CELL_SX}>
				<RowActions
					isEditing={isEditing}
					isLocked={isLocked}
					canEdit={editable.canStartEdit}
					editTitle="Edit bill"
					deleteTitle="Delete bill"
					blockedReason={blockedReason}
					isReasonShown={editable.isReasonShown}
					testId="bill"
					onEdit={() => editable.startEdit()}
					onDelete={onDelete}
					onCancel={onCancel}
					onSave={editable.save}
				/>
			</TableCell>
		</TableRow>
	);
};
